import { useEffect } from 'react';
import { useClient } from '@/contexts/ClientContext';

// Default Neon Night theme colors (fallback - dark mode)
const NEON_NIGHT_DEFAULTS = {
  primary: '#00ff88',
  secondary: '#00d4ff',
  background: '#180d2b',
  text: '#f8fafc',
};

// Convert hex to HSL for Tailwind compatibility
const hexToHSL = (hex: string): string => {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Handle shorthand hex
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }
  
  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  // Find min and max
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

// Determine if a color is "light" (for contrast purposes)
const isLightColor = (hex: string): boolean => {
  hex = hex.replace('#', '');
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  // Luminance calculation
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5;
};

// Lighten or darken a hex color
const adjustColor = (hex: string, amount: number): string => {
  hex = hex.replace('#', '');
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }
  
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  
  r = Math.max(0, Math.min(255, r + amount));
  g = Math.max(0, Math.min(255, g + amount));
  b = Math.max(0, Math.min(255, b + amount));
  
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

// ---- WCAG contrast helpers -------------------------------------------------
const hexToRgb = (hex: string): [number, number, number] => {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  return [parseInt(h.substring(0, 2), 16), parseInt(h.substring(2, 4), 16), parseInt(h.substring(4, 6), 16)];
};

const relativeLuminance = (hex: string): number => {
  const [r, g, b] = hexToRgb(hex).map(v => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const contrastRatio = (a: string, b: string): number => {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

const mixHex = (from: string, to: string, t: number): string => {
  const a = hexToRgb(from);
  const b = hexToRgb(to);
  const c = a.map((v, i) => Math.round(v + (b[i] - v) * t));
  return `#${c.map(v => v.toString(16).padStart(2, '0')).join('')}`;
};

/**
 * Brand colors are picked for surfaces (buttons, rings), not for text: a lime
 * or pastel primary on a white page is unreadable. Darken (light themes) or
 * lighten (dark themes) the primary until it reaches WCAG AA (4.5:1) against
 * the page background. Used for headings and links as `--primary-ink`.
 */
const ensureTextContrast = (fg: string, bg: string, isLightTheme: boolean): string => {
  let color = fg;
  const target = isLightTheme ? '#000000' : '#ffffff';
  for (let i = 0; i < 30 && contrastRatio(color, bg) < 4.5; i++) {
    color = mixHex(color, target, 0.07);
  }
  return color;
};

export const DynamicBranding = () => {
  const { clientSettings } = useClient();

  useEffect(() => {
    const root = document.documentElement;
    
    // Get colors from client settings or defaults
    const primaryColor = clientSettings?.brand_primary_color || NEON_NIGHT_DEFAULTS.primary;
    const secondaryColor = clientSettings?.brand_secondary_color || NEON_NIGHT_DEFAULTS.secondary;
    const backgroundColor = clientSettings?.brand_background_color || NEON_NIGHT_DEFAULTS.background;
    const textColor = clientSettings?.brand_text_color || NEON_NIGHT_DEFAULTS.text;
    
    // Determine if this is a light or dark theme based on background
    const isLightTheme = isLightColor(backgroundColor);
    
    // Theme mode flag for CSS branches (shadows, tiles)
    root.setAttribute('data-theme', isLightTheme ? 'light' : 'dark');

    // Contrast-safe brand color for text, and the surface tiles sit on
    root.style.setProperty('--primary-ink', hexToHSL(ensureTextContrast(primaryColor, backgroundColor, isLightTheme)));

    // Apply primary and secondary colors
    root.style.setProperty('--primary', hexToHSL(primaryColor));
    root.style.setProperty('--secondary', hexToHSL(secondaryColor));
    
    // Apply background and foreground
    root.style.setProperty('--background', hexToHSL(backgroundColor));
    root.style.setProperty('--foreground', hexToHSL(textColor));
    
    // Generate card color (slightly different from background)
    const cardColor = isLightTheme 
      ? adjustColor(backgroundColor, -10) // Slightly darker for light themes
      : adjustColor(backgroundColor, 15);  // Slightly lighter for dark themes
    root.style.setProperty('--card', hexToHSL(cardColor));
    root.style.setProperty('--card-foreground', hexToHSL(textColor));
    // Surface: pure white tiles on light themes read as "objects" on the tinted page
    root.style.setProperty('--surface', isLightTheme ? '0 0% 100%' : hexToHSL(cardColor));
    
    // Popover same as card
    root.style.setProperty('--popover', hexToHSL(cardColor));
    root.style.setProperty('--popover-foreground', hexToHSL(textColor));
    
    // Primary foreground (text on primary buttons) - should contrast with primary
    const primaryForeground = isLightColor(primaryColor) ? '#1a1a1a' : '#fafafa';
    root.style.setProperty('--primary-foreground', hexToHSL(primaryForeground));
    
    // Secondary foreground
    const secondaryForeground = isLightColor(secondaryColor) ? '#1a1a1a' : '#fafafa';
    root.style.setProperty('--secondary-foreground', hexToHSL(secondaryForeground));
    
    // Muted colors
    const mutedColor = isLightTheme 
      ? adjustColor(backgroundColor, -20) 
      : adjustColor(backgroundColor, 25);
    const mutedForeground = isLightTheme 
      ? adjustColor(textColor, 80) // Lighter text for muted in light theme
      : adjustColor(textColor, -80); // Darker text for muted in dark theme
    root.style.setProperty('--muted', hexToHSL(mutedColor));
    root.style.setProperty('--muted-foreground', hexToHSL(mutedForeground));
    
    // Accent (based on primary but softer)
    root.style.setProperty('--accent', hexToHSL(isLightTheme ? adjustColor(primaryColor, 40) : adjustColor(primaryColor, -30)));
    root.style.setProperty('--accent-foreground', hexToHSL(textColor));
    
    // Border and input
    const borderColor = isLightTheme 
      ? adjustColor(backgroundColor, -30) 
      : adjustColor(backgroundColor, 40);
    root.style.setProperty('--border', hexToHSL(borderColor));
    root.style.setProperty('--input', hexToHSL(cardColor));
    root.style.setProperty('--ring', hexToHSL(primaryColor));
    
    // Sidebar colors
    const sidebarBg = isLightTheme 
      ? adjustColor(backgroundColor, -5) 
      : adjustColor(backgroundColor, 5);
    root.style.setProperty('--sidebar-background', hexToHSL(sidebarBg));
    root.style.setProperty('--sidebar-foreground', hexToHSL(textColor));
    root.style.setProperty('--sidebar-primary', hexToHSL(primaryColor));
    root.style.setProperty('--sidebar-primary-foreground', hexToHSL(primaryForeground));
    root.style.setProperty('--sidebar-accent', hexToHSL(mutedColor));
    root.style.setProperty('--sidebar-accent-foreground', hexToHSL(textColor));
    root.style.setProperty('--sidebar-border', hexToHSL(borderColor));
    root.style.setProperty('--sidebar-ring', hexToHSL(primaryColor));
    
    // Update neon glow effects based on primary color
    const glowColor = primaryColor;
    root.style.setProperty('--neon-glow', `0 0 15px ${glowColor}66`);
    root.style.setProperty('--neon-glow-subtle', `0 0 10px ${glowColor}40`);
    root.style.setProperty('--neon-glow-strong', `0 0 20px ${glowColor}80, 0 0 40px ${glowColor}4d`);
    
    // Update gradient
    root.style.setProperty('--gradient-neon', `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`);
    
    // Glassmorphism - adjust based on theme
    const glassOpacity = isLightTheme ? '0.7' : '0.7';
    root.style.setProperty('--glass-bg', isLightTheme 
      ? `rgba(255, 255, 255, ${glassOpacity})`
      : `rgba(31, 17, 57, ${glassOpacity})`);
    root.style.setProperty('--glass-border', isLightTheme 
      ? 'rgba(0, 0, 0, 0.1)'
      : 'rgba(255, 255, 255, 0.1)');

    return () => {
      // Reset to CSS defaults when component unmounts
      const propsToRemove = [
        '--primary', '--secondary', '--background', '--foreground',
        '--card', '--card-foreground', '--popover', '--popover-foreground',
        '--primary-foreground', '--secondary-foreground',
        '--muted', '--muted-foreground', '--accent', '--accent-foreground',
        '--border', '--input', '--ring',
        '--sidebar-background', '--sidebar-foreground', '--sidebar-primary',
        '--sidebar-primary-foreground', '--sidebar-accent', '--sidebar-accent-foreground',
        '--sidebar-border', '--sidebar-ring',
        '--neon-glow', '--neon-glow-subtle', '--neon-glow-strong',
        '--gradient-neon', '--glass-bg', '--glass-border',
        '--primary-ink', '--surface'
      ];
      propsToRemove.forEach(prop => root.style.removeProperty(prop));
      root.removeAttribute('data-theme');
    };
  }, [clientSettings]);

  return null;
};

export default DynamicBranding;
